import { Dispatch, SetStateAction } from 'react';
import { AccordionItem } from '@components/accordion-list/src/accordion-list.type';
import { TicketHolder } from '@components/customers/customers-table-expandable/src/customers-table-expandable.type';
import { EventAreaChartSectionData } from '@components/event-area-chart-section/src/event-area-chart-section.type';
import { EventLinearChartSectionData } from '@components/event-linear-chart-section/src/event-linear-chart-section.type';
import { OfferTableSectionDataType } from '@components/event-offer-table-section/src/event-offer-table-section.type';
import { ChannelSectionDataType } from '@components/event-report-channel-section/src/event-report-channel-section.type';
import { HightLightStat } from '@components/event-report-highlight-section/src/event-report-highlight-section.type';
import { BarDataProps } from '@components/horizontal-percentage-bar/src/horizontal-percentage-bar.type';
import { EventChannel } from '@domain/channel';
import { Event } from '@domain/event';
import {
  EventAttendeesStatsResponse,
  EventReportDataType,
  EventSalesStatsAddon,
  EventSalesStatsOffer,
  EventSalesStatsResponse,
  LinearGraphGroupBy,
} from '@domain/event-report';
import { EventTeam } from '@domain/eventTeam';
import { EventBookingParams } from '@hooks/events/use-get-event-bookings';
import { EventSalesStatsGroupByResponse } from '@hooks/events/use-get-event-sales-stats-groupby';
import { ExportType, SeparatorType } from '@hooks/use-customers-page-reducer/use-customers-page-reducer-types';
import { ChannelDataTypeTabs } from '@pages/events/utils/reports-utils/config';
import {
  BookingActions,
  EventReportAreaTypes,
  EventReportLinearTypes,
  SelectedBookingEmail,
} from '@pages/events/utils/reports-utils/types';

export const TODAY_OPTION = 'today';
export const YESTERDAY_OPTION = 'yesterday';
export const PAST_WEEK_OPTION = 'pastWeek';
export const PAST_MONTH_OPTION = 'pastMonth';
export const ALL_TIME_OPTIONS = 'allTime';
export const SELECT_RANGE_OPTIONS = 'selectRange';

export type EventReportOptions =
  | typeof TODAY_OPTION
  | typeof YESTERDAY_OPTION
  | typeof PAST_WEEK_OPTION
  | typeof PAST_MONTH_OPTION
  | typeof ALL_TIME_OPTIONS
  | typeof SELECT_RANGE_OPTIONS;

export const pastTimeOptionsActions: EventReportOptions[] = [ALL_TIME_OPTIONS, SELECT_RANGE_OPTIONS];

export const timeOptionsActions: EventReportOptions[] = [
  TODAY_OPTION,
  YESTERDAY_OPTION,
  PAST_WEEK_OPTION,
  PAST_MONTH_OPTION,
  ALL_TIME_OPTIONS,
  SELECT_RANGE_OPTIONS,
];
export interface EventReportTimeOption {
  action: EventReportOptions;
  name: string;
}

export interface EventReportQueryParams {
  channels?: string[] | null;
  dateTime?: string | null;
  from?: string | null;
  to?: string | null;
  bookings?: string | null;
}

export const ALL_BOOKINGS_OPTION = 'total';
export const OFFLINE_OPTION = 'offline';
export const ONLINE_OPTION = 'online';

export type EventReportBookingOptions = typeof ALL_BOOKINGS_OPTION | typeof OFFLINE_OPTION | typeof ONLINE_OPTION;

export const bookingOptionsActions: EventReportBookingOptions[] = [ALL_BOOKINGS_OPTION, OFFLINE_OPTION, ONLINE_OPTION];

export interface EventReportBookingOption {
  action: EventReportBookingOptions;
  name: string;
}
export interface EventReportFilter {
  channels?: string[];
  from?: number;
  to?: number;
  booking?: EventReportBookingOptions;
}

export interface EventBookingMixSectionData {
  title: string;
  subtitle: string;
  total: number;
  type: string;
  horizontalBarData: BarDataProps[];
}

export interface EventBookingMixSectionCategories {
  id: EventReportDataType;
  color: string;
  label: string;
  isActive: boolean;
}
export interface EventBookingMixSectionInfo {
  data: EventBookingMixSectionData[];
  categories: EventBookingMixSectionCategories[];
}

export interface EventBookingFormatted {
  id: string;
  eventId: string;
  name: string;
  legacyId: number;
  offer: {
    name: string;
    description: string;
    type: string;
  };
  quantity: number;
  price: string;
  createdAt: string;
  status: string;
  addons: string;
  isNominative: boolean;
  deppendsOn: number | null;
  actions: BookingActions;
  isActive: boolean;
  isRefunded: boolean;
  isClicked: boolean;
  isSwapped: boolean;
  code: string | null;
  attendees: string;
  attendeesStatus: string;
}

export interface BookingTableSectionData {
  data: EventBookingFormatted[];
  filters: AccordionItem[];
  hasNextPage?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  exportType: ExportType;
  separatorType: SeparatorType;
  fetchNextPage?: () => void;
  setExportType: Dispatch<SetStateAction<ExportType>>;
  setSeparatorType: Dispatch<SetStateAction<SeparatorType>>;
}

export interface EventReportContextValue {
  attendeesStats?: EventAttendeesStatsResponse;
  event?: Event;
  eventIsLoading: boolean;
  eventSales?: EventSalesStatsResponse;
  eventSalesIsLoading: boolean;
  eventChannels?: EventChannel[];
  eventTeams?: EventTeam;
  offerTableSectionData: OfferTableSectionDataType;
  bookingMixSectionData: EventBookingMixSectionInfo;
  hightlightSectionData: HightLightStat[];
  areaChartSectionData: EventAreaChartSectionData;
  lineChartSectionData: EventLinearChartSectionData;
  filter?: EventReportFilter;
  eventSalesGroupBy?: LinearGraphGroupBy;
  eventSalesGroupedBy?: EventSalesStatsGroupByResponse;
  eventSalesGroupedByIsLoading: boolean;
  eventViewsIsLoading: boolean;
  channelSectionData: ChannelSectionDataType;
  activeChannelTab: ChannelDataTypeTabs;
  bookingTableSectionData: BookingTableSectionData;
  exportData?: Blob;
  isExportBookingLoading: boolean;
  bookingTableParams: EventBookingParams;
  eventSalesChannelsIsLoading: boolean;
  attendeesStatsIsLoading: boolean;
  timeOptions: EventReportTimeOption[];
  timeOptionSelected?: EventReportTimeOption;
  bookingOptions: EventReportBookingOption[];
  bookingOptionSelected?: EventReportBookingOption;
  selectedChannels: string[];
  activeLinearTabDataType: EventReportLinearTypes;
  activeAreaTabDataType: EventReportAreaTypes;
  isAddonModalOpen: boolean;
  isCancelModalOpen: boolean;
  setIsCancelModalOpen: Dispatch<SetStateAction<boolean>>;
  isPassHoldersModalOpen: boolean;
  selectedOffer?: EventSalesStatsOffer | EventSalesStatsAddon;
  selectedBookingCancel?: string;
  selectedBookingEmail?: SelectedBookingEmail;
  refetchExportFile?: () => void;
  setActiveChannelTab: Dispatch<SetStateAction<ChannelDataTypeTabs>>;
  setActiveLinearTabDataType: Dispatch<SetStateAction<EventReportLinearTypes>>;
  setActiveAreaTabDataType: Dispatch<SetStateAction<EventReportAreaTypes>>;
  setBookingTableParams: Dispatch<SetStateAction<EventBookingParams>>;
  setEventSalesGroupBy: Dispatch<SetStateAction<LinearGraphGroupBy>>;
  setFilters: (filters: EventReportFilter) => void;
  setQueryParams: (params: EventReportQueryParams) => void;
  toggleModalBy: ({ id, type }: { id?: string; type?: EventReportDataType }) => void;
  setSelectedBookingEmail: Dispatch<SetStateAction<SelectedBookingEmail | undefined>>;
  refundData: {
    isOpenModal: boolean;
    data?: {
      holderCode?: string;
      quantity: number;
      bookingId: string;
      legacyId?: number;
      ticketHolders: TicketHolder[];
      onlinePrice: number;
      offerType: string;
      isNominative: boolean;
      buyerFirstName: string;
      buyerLastName: string;
    };
  };
  setRefundData: Dispatch<
    SetStateAction<{
      isOpenModal: boolean;
      data?: {
        holderCode?: string;
        quantity: number;
        bookingId: string;
        legacyId?: number;
        ticketHolders: TicketHolder[];
        onlinePrice: number;
        offerType: string;
        isNominative: boolean;
        buyerFirstName: string;
        buyerLastName: string;
      };
    }>
  >;
}
